// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import deletePaymentProfile from "@insite/client-framework/Store/Data/PaymentProfiles/Handlers/DeletePaymentProfile";
import updatePaymentProfile from "@insite/client-framework/Store/Data/PaymentProfiles/Handlers/UpdatePaymentProfile";
import updateEditModal from "@insite/client-framework/Store/Pages/SavedPayments/Handlers/UpdateEditModal";
import translate from "@insite/client-framework/Translate";
import { AccountPaymentProfileModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { PaymentProfilesContext } from "@insite/content-library/Pages/SavedPaymentsPage";
import Accordion from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import SavedPaymentsCardIcon from "@store/Library/Components/Toolkit/SavedPayments/SavedPaymentsCardIcon";
import SelectableCard from "@store/Library/Components/Toolkit/SelectableCard";
import SelectInline, { SelectInlineStyles } from "@store/Library/Components/Toolkit/SelectInline";
import CardListHeading from "@store/Overrides/Components/CardListHeading";
import { savedPaymentsProps } from "@store/Themes/common/props/savedPayments";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
/* Starter Custom Code - End */

const mapDispatchToProps = {
    updateEditModal,
    updatePaymentProfile,
    deletePaymentProfile,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedPaymentsCardListStyles {
    headerContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    sortByGridItem?: GridItemProps;
    sortBySelect?: SelectProps;
    cardHeaderWrapper?: InjectableCss;
    cardTypeImage?: LazyImageProps;
    descriptionText?: TypographyPresentationProps;
    cardInfoContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    nameOnCardLabelText?: TypographyPresentationProps;
    nameOnCardText?: TypographyPresentationProps;
    expirationLabelText?: TypographyPresentationProps;
    expirationText?: TypographyPresentationProps;
    centerColumnGridItem?: GridItemProps;
    billingAddressLabelText?: TypographyPresentationProps;
    billingAddressText?: TypographyPresentationProps;
    rightColumnGridItem?: GridItemProps;
    linksContainer?: GridContainerProps;
    useAsDefaultGridItem?: GridItemProps;
    useAsDefaultButton?: ButtonPresentationProps;
    editLinkGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    deleteLinkGridItem?: GridItemProps;
    deleteLink?: LinkPresentationProps;
    deleteCardModal?: TwoButtonModalStyles;
    // Starter Custom Code
    sortBySelectInline?: SelectInlineStyles;
    cardListContainer?: GridContainerProps;
    cardListGridItem?: GridItemProps;
    descriptionTextNote?: TypographyPresentationProps;
    cardInfoSubContainer?: GridContainerProps;
    cardInfoSubGridItem?: GridItemProps;
    actionsWrapper?: InjectableCss;
}

export const cardListStyles: SavedPaymentsCardListStyles = {
    sortBySelectInline: {
        wrapper: {
            css: css`
                width: auto;
                font-size: ${x => x.theme.design.typography.size.smaller};
            `,
        },
    },
    cardListContainer: savedPaymentsProps.listContainer,
    cardListGridItem: savedPaymentsProps.listGridItem,
    cardHeaderWrapper: savedPaymentsProps.card?.descriptionWrapper,
    descriptionText: savedPaymentsProps.card?.descriptionText,
    descriptionTextNote: savedPaymentsProps.card?.descriptionTextNote,
    cardInfoContainer: savedPaymentsProps.card?.container,
    leftColumnGridItem: savedPaymentsProps.card?.gridItem,
    centerColumnGridItem: savedPaymentsProps.card?.gridItem,
    cardInfoSubContainer: savedPaymentsProps.card?.infoContainer,
    cardInfoSubGridItem: savedPaymentsProps.card?.infoGridItem,
    billingAddressLabelText: savedPaymentsProps.card?.infoLabel,
    billingAddressText: savedPaymentsProps.card?.infoValue,
    nameOnCardLabelText: savedPaymentsProps.card?.infoLabel,
    nameOnCardText: savedPaymentsProps.card?.infoValue,
    expirationLabelText: savedPaymentsProps.card?.infoLabel,
    expirationText: savedPaymentsProps.card?.infoValue,
    actionsWrapper: savedPaymentsProps.card?.actionsWrapper,
    editLink: savedPaymentsProps.card?.editLink,
    deleteLink: savedPaymentsProps.card?.deleteLink,
};

const styles = cardListStyles;

const getImageName = (cardType: string) => {
    if (cardType.toLowerCase() === "american express") {
        return "amex";
    }
    if (cardType.toLowerCase() === "discover") {
        return "discover";
    }
    if (cardType.toLowerCase() === "mastercard") {
        return "mc";
    }
    if (cardType.toLowerCase() === "visa") {
        return "visa";
    }
    return "";
};

const SavedPaymentsCardList: React.FC<Props> = ({ updateEditModal, updatePaymentProfile, deletePaymentProfile }) => {
    const [sortByProperty, setSortByProperty] = React.useState("maskedCardNumber");
    const sortByChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSortByProperty(event.target.value);
    };

    const toasterContext = React.useContext(ToasterContext);
    const useAsDefaultClickHandler = (paymentProfile: AccountPaymentProfileModel) => {
        if (!paymentProfile.isDefault /* Starter Custom Code */) {
            const paymentProfileToUpdate = { ...paymentProfile, isDefault: true };
            updatePaymentProfile({
                paymentProfile: paymentProfileToUpdate,
                onSuccess: () => {
                    toasterContext.addToast({ body: translate("Default Card Updated"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const editClickHandler = (paymentProfile: AccountPaymentProfileModel) => {
        updateEditModal({ paymentProfile, modalIsOpen: true });
    };

    const [deleteCardModalIsOpen, setDeleteCardModalIsOpen] = React.useState(false);
    const [paymentProfileToDelete, setPaymentProfileToDelete] = React.useState<AccountPaymentProfileModel | null>(null);
    const deleteCancelHandler = () => {
        setDeleteCardModalIsOpen(false);
    };

    const deleteSubmitHandler = () => {
        if (!paymentProfileToDelete) {
            return;
        }

        deletePaymentProfile({
            paymentProfileId: paymentProfileToDelete.id,
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Card Deleted"), messageType: "success" });
                setDeleteCardModalIsOpen(false); // Starter Custom Code
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const deleteClickHandler = (paymentProfile: AccountPaymentProfileModel) => {
        setPaymentProfileToDelete(paymentProfile);
        setDeleteCardModalIsOpen(true);
    };

    const paymentProfilesDataView = usePersistentDataView(useContext(PaymentProfilesContext)); // Starter Custom Code

    if (!paymentProfilesDataView.value) {
        return null;
    }

    const sortedAndFilteredSavedPayments = sortBy(
        paymentProfilesDataView.value, // Starter Custom Code
        sortByProperty,
    );
    if (sortedAndFilteredSavedPayments.length === 0) {
        return null;
    }

    return (
        <>
            {/* Starter Custom Code - Start */}
            <CardListHeading heading={translate("Saved Cards")} data-test-selector="cardListHeader">
                <SelectInline /* Starter Custom Code */
                    label={translate("{0}:", translate("Sort by"))}
                    labelPosition="left"
                    value={sortByProperty}
                    onChange={sortByChangeHandler}
                    data-test-selector="sortBy"
                    extendedStyles={styles.sortBySelectInline}
                >
                    <option value="maskedCardNumber">{translate("Card Number")}</option>
                    <option value="description">{translate("Card Nickname")}</option>
                    <option value="cardType">{translate("Card Type")}</option>
                </SelectInline>
            </CardListHeading>
            <GridContainer data-test-selector="cardList" {...styles.cardListContainer}>
                {sortedAndFilteredSavedPayments.map(savedPayment => {
                    return (
                        <GridItem {...styles.cardListGridItem} key={savedPayment.id.toString()}>
                            <SelectableCard
                                onSelect={() => useAsDefaultClickHandler(savedPayment)}
                                label={translate("Use as Default")}
                                selected={savedPayment.isDefault}
                            >
                                <StyledWrapper
                                    {...styles.cardHeaderWrapper}
                                    data-test-selector={`cardHeader-${savedPayment.id}`}
                                >
                                    <SavedPaymentsCardIcon cardType={savedPayment.cardType} />
                                    <Typography {...styles.descriptionText}>
                                        {savedPayment.description || savedPayment.cardType}
                                        <Typography {...styles.descriptionTextNote}>
                                            {`${savedPayment.cardType} ${translate(
                                                "ending in",
                                            )} ${savedPayment.maskedCardNumber.substring(
                                                savedPayment.maskedCardNumber.length - 4,
                                            )}`}
                                        </Typography>
                                    </Typography>
                                </StyledWrapper>
                                <GridContainer
                                    {...styles.cardInfoContainer}
                                    data-test-selector={`cardBody-${savedPayment.id}`}
                                >
                                    <GridItem {...styles.leftColumnGridItem}>
                                        <GridContainer {...styles.cardInfoSubContainer}>
                                            <GridItem {...styles.cardInfoSubGridItem}>
                                                <Typography {...styles.nameOnCardLabelText} id="nameOnCard">
                                                    {translate("Name on Card")}
                                                </Typography>
                                                <Typography {...styles.nameOnCardText} aria-labelledby="nameOnCard">
                                                    {savedPayment.cardHolderName}
                                                </Typography>
                                            </GridItem>
                                            <GridItem {...styles.cardInfoSubGridItem}>
                                                <Typography {...styles.expirationLabelText} id="expiration">
                                                    {translate("Expiration")}
                                                </Typography>
                                                <Typography {...styles.expirationText} aria-labelledby="expiration">
                                                    {savedPayment.expirationDate}
                                                </Typography>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                    <GridItem {...styles.centerColumnGridItem}>
                                        <Typography {...styles.billingAddressLabelText} id="billingAddress">
                                            {translate("Billing Address")}
                                        </Typography>
                                        <Typography {...styles.billingAddressText} aria-labelledby="billingAddress">
                                            {[
                                                savedPayment.address1,
                                                savedPayment.address2,
                                                savedPayment.address3,
                                                savedPayment.address4,
                                            ]
                                                .filter(o => !!o)
                                                .join(", ")}
                                            <br />
                                            {savedPayment.city}, {savedPayment.state}
                                            <br />
                                            {savedPayment.postalCode}
                                            <br />
                                            {savedPayment.country}
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                                <StyledWrapper {...styles.actionsWrapper}>
                                    <Link
                                        {...styles.editLink}
                                        onClick={() => editClickHandler(savedPayment)}
                                        data-test-selector="editButton"
                                    >
                                        {translate("Edit")}
                                    </Link>
                                    <Link
                                        {...styles.deleteLink}
                                        onClick={() => deleteClickHandler(savedPayment)}
                                        data-test-selector="deleteButton"
                                    >
                                        {translate("Delete")}
                                    </Link>
                                </StyledWrapper>
                            </SelectableCard>
                        </GridItem>
                    );
                })}
            </GridContainer>
            {/* Starter Custom Code - End */}
            <TwoButtonModal
                {...styles.deleteCardModal}
                modalIsOpen={deleteCardModalIsOpen}
                headlineText={translate("Delete Card")}
                messageText={translate("Are you sure you want to delete this card?")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={deleteCancelHandler}
                onSubmit={deleteSubmitHandler}
                submitTestSelector="submitDeleteCardButton"
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(SavedPaymentsCardList),
    definition: {
        displayName: "Card List",
        group: "Saved Payments",
        allowedContexts: ["SavedPaymentsPage"],
    },
};

export default widgetModule;
